import React from 'react';
import { clsx } from 'clsx';
import { formatDecimalParts } from '@/utils/decimal-display';
import {
  FIGURE_TIER_CLASS,
  FIGURE_TONE_CLASS,
  type FigureTier,
  type FigureTone,
} from './figure-styles';

interface FigureProps {
  /** Backend Decimal STRING (HON-03). A JS number is coerced with no float math. */
  value: string | number | null | undefined;
  /** ISO currency code; when set, prefixes the symbol ("$"). */
  currency?: string;
  /** Display fraction digits (default 2 = cent). */
  dp?: number;
  /** Demoted unit tail rendered faint alongside the cents ('/sh', '/day', '/yr'). */
  unit?: string;
  /** Size tier — a figure's size encodes its rank (default 'body'). */
  tier?: FigureTier;
  tone?: FigureTone;
  /**
   * When true (default) the fraction tail (cents) renders demoted (.lr-dec) so
   * the magnitude reads first; set false to keep cents full-size (e.g. a small
   * hero P&L where the cents still matter).
   */
  demoteDecimals?: boolean;
  /**
   * Demote the fraction tail gently (0.85em) instead of the default 0.58em.
   * For documents that print an amount rather than headline a KPI - a statement
   * line's cents are part of the figure the counterparty is invoiced, not a
   * footnote to it.
   */
  softDecimals?: boolean;
  /** Rendered when the value is null/empty/malformed (default em dash). */
  emptyDisplay?: React.ReactNode;
  className?: string;
}

/**
 * Tabular-numeral figure with a demoted decimal/unit tail — the core
 * number-priority primitive (DESIGN-SPEC §2). Formats off backend Decimal
 * strings via `formatDecimalParts` (no parseFloat — HON-03) and renders the
 * integer body full-size with the cents + unit at 0.58em faint.
 */
export function Figure({
  value,
  currency,
  dp,
  unit,
  tier = 'body',
  tone = 'default',
  demoteDecimals = true,
  softDecimals = false,
  emptyDisplay = '—',
  className,
}: FigureProps) {
  const parts = formatDecimalParts(value, { currency, dp });
  const wrapClass = clsx('lr-num', FIGURE_TIER_CLASS[tier], FIGURE_TONE_CLASS[tone], className);
  const decClass = clsx('lr-dec', softDecimals && 'lr-dec-soft');

  if (parts.isEmpty) {
    return <span className={wrapClass}>{emptyDisplay}</span>;
  }

  const head = `${parts.sign}${parts.currencySymbol}${parts.intGrouped}`;
  const fracStr = parts.dp > 0 ? `.${parts.frac}` : '';

  return (
    <span className={wrapClass}>
      {head}
      {demoteDecimals ? (
        (fracStr || unit) && (
          <span className={decClass}>
            {fracStr}
            {unit}
          </span>
        )
      ) : (
        <>
          {fracStr}
          {unit && <span className={decClass}>{unit}</span>}
        </>
      )}
    </span>
  );
}
